import React from "react";
import { Table, Tag, Button, Tooltip, Space } from "antd";
import {
  EyeOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { getPaymentPurposeLabel } from "../../../utils/paymentPurpose";

const PaymentsTable = ({
  payments,
  loading,
  onViewDetails,
  onValidate,
  onVoid,
  canVoid = false,
  onAllocateMissing,
  selectedRowKeys = [],
  onSelectedRowKeysChange,
  pagination,
  onPaginationChange,
}) => {
  const getStatusColor = (status) => {
    const statusColors = {
      Valid: "green",
      "Pending Validation": "orange",
      Suspense: "red",
      Invalid: "volcano",
      Voided: "default",
    };
    return statusColors[status] || "default";
  };

  const getValidationStatusColor = (validationStatus) => {
    const statusColors = {
      "Auto Validated": "cyan",
      "Manual Review": "blue",
      Rejected: "red",
    };
    return statusColors[validationStatus] || "default";
  };

  const getPurposeColor = (purpose) => {
    const purposeColors = {
      "Tuition Fees": "blue",
      "Custom Fee": "cyan",
      "Transport Fees": "purple",
      "BOT Clearance": "green",
      "Admission Fees": "orange",
    };
    return purposeColors[purpose] || "default";
  };

  const columns = [
    {
      title: "Reference",
      dataIndex: "paymentReference",
      key: "paymentReference",
      width: 250,
      ellipsis: true,
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => onViewDetails(record)}
          style={{ padding: 0, wordBreak: "break-word" }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: "Student",
      key: "student",
      width: 220,
      render: (_, record) => {
        const verifiedName = record.studentName || record.student?.name;
        const reportedName = record.reportedStudentName;
        const surePayCode = record.surePayStudentCode;

        return (
          <div>
            <div style={{ fontWeight: 500 }}>
              {verifiedName || reportedName || "-"}
              {!verifiedName && reportedName && (
                <Tag color="orange" style={{ marginLeft: 6 }}>
                  Unmatched · SurePay provided
                </Tag>
              )}
            </div>
            {!verifiedName && surePayCode && (
              <div style={{ color: "#8c8c8c", fontSize: 12 }}>
                SurePay code: {surePayCode}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Purpose",
      dataIndex: "purpose",
      key: "purpose",
      width: 130,
      render: (purpose, record) => {
        const displayPurpose = getPaymentPurposeLabel(
          purpose,
          record.paymentPurpose,
        );
        return (
          <Tag color={getPurposeColor(displayPurpose)}>{displayPurpose}</Tag>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 150,
      align: "right",
      render: (amount) => (
        <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
          {parseFloat(amount).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      width: 150,
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY HH:mm") : "-"),
    },
    {
      title: "Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: 120,
      render: (method) => <Tag>{method}</Tag>,
    },
    {
      title: "Channel",
      dataIndex: "paymentChannel",
      key: "paymentChannel",
      width: 120,
      render: (channel) => channel || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: "Validation Status",
      dataIndex: "validationStatus",
      key: "validationStatus",
      width: 160,
      render: (status) => (
        <Tag color={getValidationStatusColor(status)}>{status || "-"}</Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY HH:mm") : "-"),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => onViewDetails(record)}
            />
          </Tooltip>
          {(record.status === "Pending Validation" ||
            record.status === "Suspense") && (
            <Tooltip title="Validate Payment">
              <Button
                type="link"
                icon={<CheckCircleOutlined />}
                style={{ color: "#52c41a" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onValidate(record);
                }}
              />
            </Tooltip>
          )}
          {record.status === "Valid" && (
            <Tooltip title="Apply missing allocation">
              <Button
                type="link"
                icon={<SyncOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onAllocateMissing(record);
                }}
              />
            </Tooltip>
          )}
          {canVoid && record.isManualPayment && record.status !== "Voided" && (
            <Tooltip title="Void manual payment">
              <Button
                type="link"
                danger
                icon={<StopOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onVoid(record);
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const validPaymentIds = payments
    .filter((payment) => payment.status === "Valid")
    .map((payment) => payment.paymentId);

  return (
    <Table
      columns={columns}
      dataSource={payments}
      rowKey="paymentId"
      loading={loading}
      rowSelection={
        onSelectedRowKeysChange
          ? {
              selectedRowKeys,
              preserveSelectedRowKeys: true,
              onChange: (nextSelectedRowKeys, _selectedRows, info) => {
                if (info?.type === "all") {
                  const selectingAll =
                    nextSelectedRowKeys.length > selectedRowKeys.length;
                  onSelectedRowKeysChange(
                    selectingAll
                      ? validPaymentIds
                      : selectedRowKeys.filter(
                          (key) => !validPaymentIds.includes(key),
                        ),
                  );
                  return;
                }
                onSelectedRowKeysChange(nextSelectedRowKeys);
              },
              onSelectAll: (selected) => {
                if (selected) {
                  onSelectedRowKeysChange(validPaymentIds);
                  return;
                }
                onSelectedRowKeysChange(
                  selectedRowKeys.filter(
                    (key) => !validPaymentIds.includes(key),
                  ),
                );
              },
              getCheckboxProps: (record) => ({
                disabled: record.status !== "Valid",
                name: record.paymentReference,
              }),
            }
          : undefined
      }
      scroll={{ x: 1650 }}
      onChange={(nextPagination, _filters, _sorter, extra) => {
        if (extra?.action === "paginate" && onPaginationChange) {
          onPaginationChange(nextPagination.current, nextPagination.pageSize);
        }
      }}
      pagination={{
        current: pagination?.current ?? 1,
        pageSize: pagination?.pageSize ?? 10,
        total: pagination?.total ?? payments.length,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => `Total ${total} payments`,
        pageSizeOptions: ["10", "20", "50", "100"],
      }}
    />
  );
};

export default PaymentsTable;
